import {
    doppeActionTypeCreateSettingsResolvingFunction,
    doppeActionTypeDefineMetadata
} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    doppePremiumFeatureIsDisabled,
    DoppePremiumFeatures
} from '../../client-server-common';
import type {HideableValue} from '@wix/devzai-utils-common';
import {hideableValueCreateHidden} from '@wix/devzai-utils-common';
import type {DoppeActionPrice} from '../../client-server-common/types/doppe-action-price';

export namespace DoppeLockActionTypePayment {

    export interface ActionSettings {
        /**
         * Customer-facing name of the product the buyer is paying for to
         * unlock the locked content. This is rendered as the widget header
         * above the description and used as the line-item product name in
         * Wix Checkout (shown in the cart, on the order, and in checkout /
         * confirmation emails). Falls back to `action.name` then to a
         * generic locale-based default when empty.
         */
        productName: string;
        /**
         * The price the user is required to pay through Wix Checkout to
         * unlock the locked action. The currency is resolved at runtime from
         * the connected Wix site's default payment currency.
         */
        priceData: DoppeActionPrice;
        /**
         * Static note configured by the creator that is forwarded to Wix
         * Checkout as the buyer note. The viewer also appends a localized
         * "how to unlock" sentence so buyers always see the order-id flow,
         * regardless of what the creator typed here.
         */
        unlockNote: string;
        /**
         * Label for the "Pay to unlock" button shown in the viewer.
         */
        buttonText: HideableValue<string>;
        /**
         * Label for the "Already paid? Unlock with order details" button
         * shown in the viewer.
         */
        unlockButtonText: HideableValue<string>;
    }

    export interface UnlockMetadata {
        orderNumber: string;
        email: string;
    }
}

export const doppeLockActionTypePayment = doppeActionTypeDefineMetadata<DoppeLockActionTypePayment.ActionSettings>({
    id: 'co.hopp.lockAction/payment',
    resolveActionSettings: doppeActionTypeCreateSettingsResolvingFunction({
        productName: '',
        priceData: {
            actualPrice: 5,
            currencyCode: 'USD',
        },
        unlockNote: '',
        buttonText: hideableValueCreateHidden(''),
        unlockButtonText: hideableValueCreateHidden(''),
    }),
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            productName: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
            unlockNote: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
            buttonText: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityActionButtonText),
            unlockButtonText: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityActionButtonText),
        },
        supportsActionCtaButtons: false,
        supportsContentSections: false,
        hideableContentSettingsNames: {
            buttonText: true,
            unlockButtonText: true,
        },
        bindableContentSettingsNames: {}
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    isLinkOpener: false,
    isLockAction: true,
    isPaymentAction: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    isPremiumAction: (premiumFeaturesStatus) => {
        return doppePremiumFeatureIsDisabled(premiumFeaturesStatus, DoppePremiumFeatures.creators_monetization_actions);
    },
});
